import React, { useState, useEffect, useMemo } from 'react';
import { GuideRow } from './GuideRow';
import { EditGuideModal } from './EditGuideModal';
import { useGuidesStore } from '../../stores/guidesStore';
import { Guide } from '../../types/guide';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { useConfirm } from '../../hooks/useConfirm';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { Download, UserCog, Users, Building2, Phone, UserCheck, Import as Passport, AlertCircle, Trash2, Edit, Search, Filter, RefreshCw, ShieldCheck, FileSpreadsheet, FileImage, ExternalLink, Upload } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useOrganizersStore } from '../../stores/organizersStore';
import { motion } from 'framer-motion';
import { usePilgrimGuideStore } from '../../stores/pilgrimGuideStore';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

export const GuidesList = () => {
  const { guides, loading, error, initialized, initializeListener, deleteGuide, updateGuide } = useGuidesStore();
  const { organizers, initialized: organizersInitialized, fetchOrganizers } = useOrganizersStore();
  const [editingGuide, setEditingGuide] = useState<Guide | null>(null);
  const { isOpen, config, confirm, close } = useConfirm();
  const { translations } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [organizerFilter, setOrganizerFilter] = useState('');
  const [completionFilter, setCompletionFilter] = useState('all'); // 'all', 'complete', 'incomplete'
  const [verifiedFilter, setVerifiedFilter] = useState('all'); // 'all', 'verified', 'unverified'
  const { links, initialized: linksInitialized, fetchLinks } = usePilgrimGuideStore();
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  
  // Initialize listeners when component mounts
  useEffect(() => {
    if (!initialized) {
      const cleanup = initializeListener();
      return () => cleanup();
    }
  }, [initialized, initializeListener]);

  useEffect(() => {
    if (!organizersInitialized) {
      fetchOrganizers();
    }
  }, [organizersInitialized, fetchOrganizers]);

  useEffect(() => {
    if (!linksInitialized) {
      fetchLinks();
    }
  }, [linksInitialized, fetchLinks]);

  const handleDelete = async (guide: Guide) => {
    try {
      await deleteGuide(guide.id);
      toast.success(translations?.guidesList?.deleteSuccess || 'Guide deleted successfully');
    } catch (error: any) {
      console.error('Error deleting guide:', error);
      toast.error(error.message || translations?.guidesList?.deleteError || 'Failed to delete guide');
    }
  };

  const confirmDelete = (guide: Guide) => {
    confirm({
      title: translations?.guidesList?.deleteTitle || 'Delete Guide',
      message: `${translations?.guidesList?.deleteMessage || 'Are you sure you want to delete the guide'} "${guide.name}"?`,
      onConfirm: () => handleDelete(guide),
      type: 'danger',
      confirmText: translations?.guidesList?.confirmDelete || 'Delete',
      cancelText: translations?.guidesList?.cancelDelete || 'Cancel',
    });
  };

  // Toggle verification status
  const handleToggleVerification = async (guide: Guide) => {
    try {
      const userId = localStorage.getItem('userId');
      const userName = localStorage.getItem('userName') || 'Admin';
      
      await updateGuide(guide.id, {
        verified: !guide.verified,
        verifiedAt: !guide.verified ? Date.now() : undefined,
        verifiedBy: !guide.verified ? userId || undefined : undefined
      });
      
      toast.success(guide.verified ? 'Guide verification removed' : 'Guide verified successfully');
    } catch (error: any) {
      console.error('Error toggling verification:', error);
      toast.error('Failed to update verification status');
    }
  };

  // Helper function to extract clean filename from URL
  const getCleanFilename = (url: string, passportNumber: string | undefined): string => {
    try {
      // Extract just the base filename without query parameters
      let filename = url.split('?')[0]; // Remove query parameters
      
      // Get the extension from the remaining path
      let extension = filename.split('.').pop();
      
      // If we couldn't determine an extension, default to jpg
      if (!extension || extension.includes('/')) {
        extension = 'jpg';
      }
      
      // Use passport number if available, otherwise use a timestamp
      const baseName = passportNumber || Date.now().toString();
      
      return `${baseName}.${extension}`;
    } catch (error) {
      console.warn('Error parsing filename:', error);
      // Fallback filename
      return `${passportNumber || 'passport'}.jpg`;
    }
  };

  // Download individual passport photo
  const downloadPassportPhoto = (guide: Guide) => {
    if (!guide.passportPhotoUrl) {
      toast.error('No passport photo available');
      return;
    }

    try {
      // Get a clean filename without query parameters
      const cleanFilename = getCleanFilename(guide.passportPhotoUrl, guide.passportNumber);
      
      fetch(guide.passportPhotoUrl)
        .then(response => response.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = cleanFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          toast.success('Passport photo downloaded successfully');
        })
        .catch(error => {
          console.error('Error downloading file:', error);
          toast.error('Failed to download passport photo');
        });
    } catch (error) {
      console.error('Error initiating download:', error);
      toast.error('Failed to download passport photo');
    }
  };

  // Download all passport photos as a zip
  const downloadAllPassportPhotos = async () => {
    try {
      // Get all guides with passport photos
      const guidesWithPhotos = filteredGuides.filter(guide => guide.passportPhotoUrl);
      
      if (guidesWithPhotos.length === 0) {
        toast.error('No passport photos to download');
        return;
      }
      
      setIsDownloadingAll(true);
      toast.loading('Preparing passport photos for download...', { id: 'download-zip' });
      
      // Create a new zip file
      const zip = new JSZip();
      
      // Track progress
      let processedCount = 0;
      let errorCount = 0;
      
      // Process photos in small batches to avoid memory issues
      const batchSize = 3; // Reduced batch size for better performance
      
      for (let i = 0; i < guidesWithPhotos.length; i += batchSize) {
        const batch = guidesWithPhotos.slice(i, i + batchSize);
        
        // Process each guide's photo in parallel within the batch
        const batchResults = await Promise.allSettled(batch.map(async (guide) => {
          try {
            const response = await fetch(guide.passportPhotoUrl!);
            
            if (!response.ok) {
              throw new Error(`HTTP error ${response.status}`);
            }
            
            const blob = await response.blob();
            
            // Get clean filename without query parameters
            const cleanFilename = getCleanFilename(guide.passportPhotoUrl!, guide.passportNumber);
            
            return { guide, blob, filename: cleanFilename };
          } catch (error) {
            console.error(`Error fetching photo for ${guide.name}:`, error);
            throw error;
          }
        }));
        
        // Add successful results to the zip file
        batchResults.forEach(result => {
          if (result.status === 'fulfilled') {
            const { guide, blob, filename } = result.value;
            zip.file(filename, blob);
            processedCount++;
          } else {
            errorCount++;
            console.error(`Failed to process photo:`, result.reason);
          }
        });
        
        // Update progress after each batch
        const progress = Math.round(((i + batch.length) / guidesWithPhotos.length) * 100);
        toast.loading(`Processing: ${progress}% complete`, { id: 'download-zip' });
        
        // Add a small delay between batches to prevent browser from freezing
        if (i + batchSize < guidesWithPhotos.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      if (processedCount === 0) {
        toast.dismiss('download-zip');
        toast.error('No passport photos could be processed');
        setIsDownloadingAll(false);
        return;
      }
      
      // Generate zip file
      toast.loading('Generating zip file...', { id: 'download-zip' });
      const content = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });
      
      // Create filename with date
      const date = new Date().toISOString().split('T')[0];
      const filename = `passport_photos_${date}.zip`;
      
      // Save the zip file
      saveAs(content, filename);
      
      toast.dismiss('download-zip');
      if (errorCount > 0) {
        toast.success(`Downloaded ${processedCount} passport photos (${errorCount} failed)`);
      } else {
        toast.success(`Downloaded ${processedCount} passport photos`);
      }
    } catch (error) {
      console.error('Error downloading photos:', error);
      toast.dismiss('download-zip');
      toast.error('Failed to download passport photos');
    } finally {
      setIsDownloadingAll(false);
    }
  };

  // Check if a guide has all fields completed
  // Updated to NOT require hajjPhone for a complete profile
  const isGuideComplete = (guide: Guide): boolean => {
    return !!(guide.passportNumber && guide.nusukEmail && guide.mainPhone && guide.passportPhotoUrl);
  };

  // Filter guides based on search term, organizer filter, completion status, and verification status
  const filteredGuides = useMemo(() => {
    return Object.values(guides).filter(guide => {
      // Don't show current guide
      const matchesSearch = !searchTerm || 
        guide.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guide.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (guide.passportNumber && guide.passportNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (guide.mainPhone && guide.mainPhone.includes(searchTerm)) ||
        (guide.hajjPhone && guide.hajjPhone.includes(searchTerm));

      // For organizer, only show their guides
      const matchesOrganizer = organizerFilter === '' || guide.organizerId === organizerFilter;

      // Completion filter
      const isComplete = isGuideComplete(guide);
      const matchesCompletion = completionFilter === 'all' || 
        (completionFilter === 'complete' && isComplete) || 
        (completionFilter === 'incomplete' && !isComplete);
        
      // Verification filter
      const matchesVerification = verifiedFilter === 'all' ||
        (verifiedFilter === 'verified' && guide.verified) ||
        (verifiedFilter === 'unverified' && !guide.verified);

      return matchesSearch && matchesOrganizer && matchesCompletion && matchesVerification;
    });
  }, [guides, searchTerm, organizerFilter, completionFilter, verifiedFilter]);

  // Calculate statistics and completion status
  const stats = useMemo(() => {
    const guidesArray = Object.values(guides);
    
    // Count guides by organizer
    const byOrganizer: Record<string, number> = {};
    guidesArray.forEach((guide) => {
      const organizerId = guide.organizerId;
      byOrganizer[organizerId] = (byOrganizer[organizerId] || 0) + 1;
    });

    // Sort organizers by guide count
    const organizersSorted = Object.entries(byOrganizer)
      .sort((a, b) => b[1] - a[1])
      .map(([id, count]) => ({
        id,
        name: organizers[id]?.name || 'Unknown Organizer',
        count
      }))
      .slice(0, 5); // Top 5 organizers

    // Count guides with contact details
    const withPassport = guidesArray.filter(g => g.passportNumber).length;
    const withMainPhone = guidesArray.filter(g => g.mainPhone).length;
    const withHajjPhone = guidesArray.filter(g => g.hajjPhone).length;
    const withNusukEmail = guidesArray.filter(g => g.nusukEmail).length;
    const withPassportPhoto = guidesArray.filter(g => g.passportPhotoUrl).length;
    
    // Count verified guides
    const verifiedGuides = guidesArray.filter(g => g.verified).length;
    
    // Count guides with complete profiles (required fields filled)
    // Updated to include passport photo
    const completeProfiles = guidesArray.filter(g => 
      g.passportNumber && g.nusukEmail && g.mainPhone && g.passportPhotoUrl
    ).length;
    
    return {
      total: guidesArray.length,
      byOrganizer: organizersSorted,
      withPassport,
      withMainPhone,
      withHajjPhone,
      withNusukEmail,
      withPassportPhoto,
      completeProfiles,
      verifiedGuides,
      completionRate: guidesArray.length > 0 ? Math.round((completeProfiles / guidesArray.length) * 100) : 0,
      verificationRate: guidesArray.length > 0 ? Math.round((verifiedGuides / guidesArray.length) * 100) : 0
    };
  }, [guides, organizers]);

  // Handle filter reset
  const resetFilters = () => {
    setSearchTerm('');
    setOrganizerFilter('');
    setCompletionFilter('all');
    setVerifiedFilter('all');
  };

  // Basic export to Excel
  const handleBasicExport = async () => {
    try {
      setIsExporting(true);
      setExportProgress(5);
      
      // Use filtered guides for export
      const guidesArray = filteredGuides;
      
      // Create data rows for export
      const exportData = guidesArray.map(guide => {
        const organizerName = organizers[guide.organizerId]?.name || 'Unknown Organizer';
        
        return {
          'Name': guide.name,
          'Email': guide.email,
          'Passport Number': guide.passportNumber || '',
          'Nusuk Email': guide.nusukEmail || '',
          'Main Phone': guide.mainPhone || '',
          'Hajj Phone': guide.hajjPhone || '',
          'Passport Photo': guide.passportPhotoUrl ? 'Yes' : 'No',
          'Organizer': organizerName, 
          'Verified': guide.verified ? 'Yes' : 'No',
          'Verified By': guide.verifiedBy || '',
          'Verified At': guide.verifiedAt ? new Date(guide.verifiedAt).toLocaleDateString() : '',
          'Status': isGuideComplete(guide) ? 'Complete' : 'Incomplete'
        };
      });

      setExportProgress(40);

      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 30 }, // Name
        { wch: 30 }, // Email
        { wch: 20 }, // Passport Number
        { wch: 30 }, // Nusuk Email
        { wch: 20 }, // Main Phone
        { wch: 20 }, // Hajj Phone
        { wch: 15 }, // Passport Photo
        { wch: 30 }, // Organizer
        { wch: 10 }, // Verified
        { wch: 20 }, // Verified By
        { wch: 15 }, // Verified At
        { wch: 15 }  // Status
      ];

      setExportProgress(70);

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Guides');

      // Generate filename with date and filters
      const date = new Date().toISOString().split('T')[0];
      let fileName = `guides-basic-export-${date}`;
      
      // Add filter info to filename
      if (organizerFilter) {
        const orgName = organizers[organizerFilter]?.name?.replace(/\s+/g, '-').toLowerCase() || 'filtered';
        fileName += `-${orgName}`;
      }
      
      if (completionFilter !== 'all') {
        fileName += `-${completionFilter}`;
      }
      
      if (verifiedFilter !== 'all') {
        fileName += `-${verifiedFilter}`;
      }
      
      fileName += '.xlsx';

      setExportProgress(90);

      // Write to file and trigger download
      XLSX.writeFile(wb, fileName);
      setExportProgress(100);
      toast.success(translations?.guidesList?.exportSuccess || 'Guides exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(translations?.guidesList?.exportError || 'Failed to export guides');
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  // Advanced export to Excel with packages information
  const handleAdvancedExport = async () => {
    try {
      setIsExporting(true);
      setExportProgress(5);
      
      // Use filtered guides for export
      const guidesArray = filteredGuides;
      
      // First gather guide IDs
      const guideIds = guidesArray.map(guide => guide.id);
      
      // Process links data to get packages per guide
      const guidesToPackages: Record<string, Map<string, number>> = {};
      
      // Initialize guide package maps
      guideIds.forEach(id => {
        guidesToPackages[id] = new Map<string, number>();
      });
      
      // Process all links to collect package names and count pilgrims per package
      setExportProgress(10);
      Object.values(links).forEach(link => {
        if (guideIds.includes(link.guideId) && link.packageName) {
          const packagesMap = guidesToPackages[link.guideId];
          packagesMap.set(
            link.packageName, 
            (packagesMap.get(link.packageName) || 0) + 1
          );
        }
      });
      
      setExportProgress(40);
      
      // Create data rows for export with packages info including counts
      const exportData = guidesArray.map(guide => {
        const organizerName = organizers[guide.organizerId]?.name || 'Unknown Organizer';
        
        // Format package names with counts: "package1 (6), package2 (20), etc."
        const packagesMap = guidesToPackages[guide.id] || new Map();
        const packageNames = Array.from(packagesMap.entries())
          .map(([name, count]) => `${name} (${count})`)
          .join(', ');
        
        return {
          'Name': guide.name,
          'Email': guide.email,
          'Passport Number': guide.passportNumber || '',
          'Nusuk Email': guide.nusukEmail || '',
          'Main Phone': guide.mainPhone || '',
          'Hajj Phone': guide.hajjPhone || '',
          'Passport Photo': guide.passportPhotoUrl ? 'Yes' : 'No',
          'Organizer': organizerName,
          'Packages': packageNames, // Packages with pilgrim counts
          'Verified': guide.verified ? 'Yes' : 'No',
          'Verified By': guide.verifiedBy || '',
          'Verified At': guide.verifiedAt ? new Date(guide.verifiedAt).toLocaleDateString() : '',
          'Status': isGuideComplete(guide) ? 'Complete' : 'Incomplete'
        };
      });

      setExportProgress(70);

      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 30 }, // Name
        { wch: 30 }, // Email
        { wch: 20 }, // Passport Number
        { wch: 30 }, // Nusuk Email
        { wch: 20 }, // Main Phone
        { wch: 20 }, // Hajj Phone
        { wch: 15 }, // Passport Photo
        { wch: 30 }, // Organizer
        { wch: 50 }, // Packages (wider for multiple package names)
        { wch: 10 }, // Verified
        { wch: 20 }, // Verified By
        { wch: 15 }, // Verified At
        { wch: 15 }  // Status
      ];

      setExportProgress(80);

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Guides');

      // Generate filename with date and filters
      const date = new Date().toISOString().split('T')[0];
      let fileName = `guides-advanced-export-${date}`;
      
      // Add filter info to filename
      if (organizerFilter) {
        const orgName = organizers[organizerFilter]?.name?.replace(/\s+/g, '-').toLowerCase() || 'filtered';
        fileName += `-${orgName}`;
      }
      
      if (completionFilter !== 'all') {
        fileName += `-${completionFilter}`;
      }
      
      if (verifiedFilter !== 'all') {
        fileName += `-${verifiedFilter}`;
      }
      
      fileName += '.xlsx';

      setExportProgress(95);

      // Write to file and trigger download
      XLSX.writeFile(wb, fileName);
      setExportProgress(100);
      toast.success('Advanced guides export completed successfully');
    } catch (error) {
      console.error('Advanced export error:', error);
      toast.error('Failed to export guides with package information');
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  // Helper function to get filename from URL
  const getFilenameFromUrl = (url: string) => {
    try {
      const pathSegments = new URL(url).pathname.split('/');
      const filename = pathSegments[pathSegments.length - 1];
      const match = filename.match(/passport_([^.]+)\.(.+)$/);
      if (match && match.length >= 3) {
        return `${match[1]}.${match[2]}`;
      }
      return filename;
    } catch (error) {
      return 'passport-photo';
    }
  };

  if (loading && !initialized) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <>
      {/* Statistics Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-4 md:p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-primary-50 rounded-lg">
              <UserCog className="text-primary-500" size={20} />
            </div>
            <div>
              <div className="text-sm text-gray-500">Total Guides</div>
              <div className="text-2xl font-bold text-primary-600">{stats.total}</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-4 md:p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Building2 className="text-blue-500" size={20} />
            </div>
            <div>
              <div className="text-sm text-gray-500">Top Organizers</div>
              <div className="text-xl font-bold text-blue-600">
                {stats.byOrganizer.length > 0 ? stats.byOrganizer[0].count : 0} guides
              </div>
            </div>
          </div>
          <div className="mt-3 space-y-2 max-h-32 overflow-y-auto">
            {stats.byOrganizer.map((org, index) => (
              <div key={org.id} className="flex justify-between items-center text-sm">
                <span className="text-gray-700 truncate max-w-[160px]">{org.name}</span>
                <span className="font-semibold">{org.count}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-4 md:p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <UserCheck className="text-green-500" size={20} />
            </div>
            <div>
              <div className="text-sm text-gray-500">Completed Profiles</div>
              <div className="text-2xl font-bold text-green-600">
                {stats.completeProfiles}
                <span className="text-sm font-normal ml-2 text-gray-500">
                  ({stats.completionRate}%)
                </span>
              </div>
            </div>
          </div>
          <div className="mt-2">
            <div className="text-xs text-gray-600">With passport photos: {stats.withPassportPhoto}</div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-4 md:p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-indigo-50 rounded-lg">
              <ShieldCheck className="text-indigo-500" size={20} />
            </div>
            <div>
              <div className="text-sm text-gray-500">Verified Guides</div>
              <div className="text-2xl font-bold text-indigo-600">
                {stats.verifiedGuides}
                <span className="text-sm font-normal ml-2 text-gray-500">
                  ({stats.verificationRate}%)
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search guides..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          </div>
          
          {/* Organizer Filter */}
          <div>
            <select
              value={organizerFilter}
              onChange={(e) => setOrganizerFilter(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Organizers</option>
              {Object.values(organizers).map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
          
          {/* Completion Filter */}
          <div>
            <select
              value={completionFilter}
              onChange={(e) => setCompletionFilter(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Completion Status</option>
              <option value="complete">Complete Profiles</option>
              <option value="incomplete">Incomplete Profiles</option>
            </select>
          </div>
          
          {/* Verification Filter */}
          <div>
            <select
              value={verifiedFilter}
              onChange={(e) => setVerifiedFilter(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Verification Status</option>
              <option value="verified">Verified Guides</option>
              <option value="unverified">Unverified Guides</option>
            </select>
          </div>
          
          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={resetFilters}
              className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
              title="Reset Filters"
            >
              <RefreshCw size={18} />
              <span className="hidden sm:inline">Reset</span>
            </button>
            
            {/* Export Dropdown */}
            <div className="relative group">
              <button
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100"
                title="Export Options"
              >
                <Download size={18} />
                <span className="hidden sm:inline">Export</span>
              </button>
              
              <div className="absolute right-0 mt-1 w-64 bg-white shadow-lg rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                <div className="py-1">
                  {/* Basic Export Option */}
                  <button
                    onClick={handleBasicExport}
                    disabled={isExporting}
                    className="px-4 py-2 w-full text-left hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Download size={16} />
                    <span>Export Guides Data</span>
                  </button>
                  
                  {/* Advanced Export Option */}
                  <button
                    onClick={handleAdvancedExport}
                    disabled={isExporting}
                    className="px-4 py-2 w-full text-left hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FileSpreadsheet size={16} />
                    <span>Export with Packages Info</span>
                  </button>
                  
                  {/* Download All Passport Photos */}
                  <button
                    onClick={downloadAllPassportPhotos}
                    disabled={isDownloadingAll || filteredGuides.filter(g => g.passportPhotoUrl).length === 0}
                    className="px-4 py-2 w-full text-left hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FileImage size={16} />
                    <span>Download All Passport Photos</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Export Progress Bar */}
        {(isExporting || isDownloadingAll) && (
          <div className="mt-3">
            <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary-500 rounded-full"
                style={{ width: `${isExporting ? exportProgress : 100}%` }}
              />
            </div>
            <div className="text-xs text-center text-gray-500 mt-1">
              {isExporting 
                ? (exportProgress < 100 ? 'Processing guides data...' : 'Export complete!')
                : 'Processing passport photos...'}
            </div>
          </div>
        )}
        
        {/* Active Filters */}
        {(searchTerm || organizerFilter || completionFilter !== 'all' || verifiedFilter !== 'all') && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="flex items-center gap-2 pt-3 border-t"
          >
            <Filter size={16} className="text-primary-500" />
            <span className="text-sm text-gray-600">
              {translations?.organizerFilters?.activeFilters || 'Active Filters:'}
            </span>
            <div className="flex flex-wrap gap-2">
              {searchTerm && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  {translations?.organizerFilters?.search || 'Search'}: {searchTerm}
                </span>
              )}
              {organizerFilter && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  {translations?.organizerFilters?.organizer || 'Organizer'}: {organizers[organizerFilter]?.name}
                </span>
              )}
              {completionFilter !== 'all' && (
                <span className={`text-xs px-2 py-1 rounded ${
                  completionFilter === 'complete' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {completionFilter === 'complete' ? 'Complete' : 'Incomplete'}
                </span>
              )}
              {verifiedFilter !== 'all' && (
                <span className={`text-xs px-2 py-1 rounded ${
                  verifiedFilter === 'verified' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {verifiedFilter === 'verified' ? 'Verified' : 'Unverified'}
                </span>
              )}
            </div>
          </motion.div>
        )}
      </div>

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">
          {filteredGuides.length} {filteredGuides.length === 1 ? 'Guide' : 'Guides'} {filteredGuides.length !== Object.keys(guides).length && `(filtered from ${Object.keys(guides).length})`}
        </h2>
      </div>

      {/* Responsive Table & Cards */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                  {translations?.guidesList?.headers?.guide || 'Guide & Organizer'}
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                  {translations?.guidesList?.headers?.contact || 'Contact Info'}
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                  {translations?.guidesList?.headers?.passport || 'Passport Photo'}
                </th>
                <th className="px-6 py-3 text-center text-sm font-medium text-gray-500 w-20">
                  {translations?.guidesList?.headers?.actions || 'Actions'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredGuides.map((guide) => (
                <tr key={guide.id} className="hover:bg-gray-50">
                  {/* Guide & Organizer Column */}
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center">
                          <span className="text-primary-600 font-medium">
                            {guide.name.charAt(0)}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="font-medium">{guide.name}</span>
                          <span className="text-sm text-gray-500 flex items-center gap-1">
                            <Building2 size={12} className="text-gray-400" />
                            {organizers[guide.organizerId]?.name || 'Unknown Organizer'}
                          </span>
                        </div>
                        {guide.verified && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 ml-2">
                            <ShieldCheck size={12} />
                            <span>Verified</span>
                          </span>
                        )}
                        {isGuideComplete(guide) ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 ml-2">
                            <UserCheck size={12} />
                            <span>Complete</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 ml-2">
                            <AlertCircle size={12} />
                            <span>Incomplete</span>
                          </span>
                        )}
                      </div>

                      {/* Email & Passport stacked */}
                      <div className="ml-11 space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <div className="text-gray-600">{guide.email}</div>
                          <button
                            onClick={() => navigator.clipboard.writeText(guide.email)}
                            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                            title="Copy Email"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                              <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                              <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                            </svg>
                          </button>
                        </div>
                        
                        {guide.passportNumber && (
                          <div className="flex items-center gap-2 text-sm">
                            <Passport size={12} className="text-gray-400" />
                            <div className="text-gray-600">{guide.passportNumber}</div>
                            <button
                              onClick={() => navigator.clipboard.writeText(guide.passportNumber!)}
                              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                              title="Copy Passport"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                                <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Contact Information */}
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-2">
                      {guide.mainPhone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone size={14} className="text-gray-400" />
                          <span className="text-gray-600">{guide.mainPhone}</span>
                          <button
                            onClick={() => navigator.clipboard.writeText(guide.mainPhone!)}
                            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                              <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                              <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                            </svg>
                          </button>
                        </div>
                      )}
                      {guide.hajjPhone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone size={14} className="text-green-400" />
                          <span className="text-gray-600">{guide.hajjPhone}</span>
                          <button
                            onClick={() => navigator.clipboard.writeText(guide.hajjPhone!)}
                            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                              <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                              <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                            </svg>
                          </button>
                        </div>
                      )}
                      {guide.nusukEmail && (
                        <div className="flex items-center gap-2 text-sm">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400">
                            <path d="M7 7h10m-10 4h10m-10 4h7"></path>
                          </svg>
                          <span className="text-gray-600 truncate max-w-[180px]">{guide.nusukEmail}</span>
                          <button
                            onClick={() => navigator.clipboard.writeText(guide.nusukEmail!)}
                            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                              <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                              <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                            </svg>
                          </button>
                        </div>
                      )}
                      {!guide.mainPhone && !guide.hajjPhone && !guide.nusukEmail && (
                        <span className="text-gray-400 text-sm italic">No contact details</span>
                      )}
                    </div>
                  </td>

                  {/* Passport Photo Column */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {guide.passportPhotoUrl ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => window.open(guide.passportPhotoUrl, '_blank')}
                            className="text-primary-600 hover:text-primary-700 flex items-center gap-1"
                          >
                            <ExternalLink size={16} />
                            <span>View</span>
                          </button>
                          <button
                            onClick={() => downloadPassportPhoto(guide)}
                            className="text-primary-600 hover:text-primary-700 flex items-center gap-1"
                          >
                            <Download size={16} />
                            <span>Download</span>
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm italic">No passport photo</span>
                      )}
                    </div>
                  </td>

                  {/* Actions Column - Vertically stacked */}
                  <td className="px-6 py-4">
                    <div className="flex flex-col items-center gap-2">
                      {/* Edit Button */}
                      <button
                        onClick={() => setEditingGuide(guide)}
                        className="p-2 text-primary-500 hover:bg-primary-50 rounded-lg"
                        title="Edit Guide"
                      >
                        <Edit size={16} />
                      </button>
                      
                      {/* Delete Button */}
                      <button
                        onClick={() => confirmDelete(guide)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        title="Delete Guide"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile List */}
        <div className="md:hidden">
          {filteredGuides.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No guides found matching your filters
            </div>
          ) : (
            filteredGuides.map((guide) => (
              <div key={guide.id} className="border-b last:border-b-0">
                <GuideRow
                  guide={guide}
                  onEdit={setEditingGuide}
                  onDelete={confirmDelete}
                />
                
                {/* Passport Photo Section - Mobile View */}
                <div className="px-4 py-2 flex flex-wrap items-center justify-between gap-2">
                  {guide.passportPhotoUrl ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-sm text-gray-600">
                        Passport Photo: {getFilenameFromUrl(guide.passportPhotoUrl)}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => window.open(guide.passportPhotoUrl, '_blank')}
                          className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1 px-2 py-1 bg-primary-50 rounded"
                        >
                          <ExternalLink size={14} />
                          <span>View</span>
                        </button>
                        <button
                          onClick={() => downloadPassportPhoto(guide)}
                          className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1 px-2 py-1 bg-primary-50 rounded"
                        >
                          <Download size={14} />
                          <span>Download</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm italic">No passport photo</span>
                  )}
                </div>
                
                {/* Status and Verification Badges - Mobile View */}
                <div className="p-4 pt-0 flex justify-between">
                  {isGuideComplete(guide) ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <UserCheck size={12} />
                      Complete
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <AlertCircle size={12} />
                      Incomplete ({4 - 
                        [guide.passportNumber, guide.nusukEmail, guide.mainPhone, guide.passportPhotoUrl]
                        .filter(Boolean).length} missing)
                    </span>
                  )}
                  
                  {/* Verification Status/Button */}
                  {guide.verified ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                      <ShieldCheck size={12} />
                      Verified
                    </span>
                  ) : (
                    <button
                      onClick={() => handleToggleVerification(guide)}
                      className="px-2 py-1 bg-gray-100 hover:bg-indigo-50 text-gray-600 hover:text-indigo-600 rounded text-xs font-medium"
                    >
                      Verify
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Empty State */}
      {filteredGuides.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="flex flex-col items-center justify-center">
            <div className="p-3 bg-gray-100 rounded-full mb-4">
              <UserCog size={24} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No Guides Found</h3>
            <p className="text-sm text-gray-500 mb-4">
              {searchTerm || organizerFilter || completionFilter !== 'all' || verifiedFilter !== 'all' ? 
                'Try adjusting your filters to find what you\'re looking for.' :
                'There are no guides in the system yet. Add a guide to get started.'
              }
            </p>
            {(searchTerm || organizerFilter || completionFilter !== 'all' || verifiedFilter !== 'all') && (
              <button
                onClick={resetFilters}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                <RefreshCw size={14} />
                <span>Reset Filters</span>
              </button>
            )}
          </div>
        </div>
      )}

      {editingGuide && (
        <EditGuideModal
          isOpen={true}
          onClose={() => setEditingGuide(null)}
          guide={editingGuide}
        />
      )}

      {isOpen && config && (
        <ConfirmDialog
          isOpen={isOpen}
          onClose={close}
          onConfirm={config.onConfirm}
          title={config.title}
          message={config.message}
          type={config.type}
          confirmText={translations?.guidesList?.confirmDelete || 'Delete'}
          cancelText={translations?.guidesList?.cancelDelete || 'Cancel'}
        />
      )}
    </>
  );
};